import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import DataTable from '../../../components/Table/DataTable.jsx';
import { KARDEX_POR_PRODUCTO, STOCK_ACTUAL } from './gql.js';

const columns = [
  { key: 'fecha', label: 'Fecha' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'referencia', label: 'Referencia' },
  { key: 'cantidad', label: 'Cantidad' },
  { key: 'costo', label: 'Costo unit.' },
  { key: 'saldoCantidad', label: 'Saldo cant.' },
  { key: 'saldoValor', label: 'Saldo valor' },
];

function formatNumber(value) {
  return Number(value ?? 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function KardexList({ onViewDetalle, onAdjust, refreshKey = 0 }) {
  const [filters, setFilters] = useState({
    productoId: '',
    sucursalId: '',
    desde: '',
    hasta: '',
  });
  const [variables, setVariables] = useState(null);
  const [formError, setFormError] = useState('');

  const [{ data, fetching, error }, reexecute] = useQuery({
    query: KARDEX_POR_PRODUCTO,
    variables: variables
      ? {
          productoId: variables.productoId,
          sucursalId: variables.sucursalId || null,
          desde: variables.desde || null,
          hasta: variables.hasta || null,
          pagination: { limit: 100, offset: 0 },
        }
      : undefined,
    pause: !variables?.productoId,
  });

  const [{ data: stockData }, reexecuteStock] = useQuery({
    query: STOCK_ACTUAL,
    variables: variables
      ? {
          productoId: variables.productoId,
          sucursalId: variables.sucursalId || null,
        }
      : undefined,
    pause: !variables?.productoId,
  });

  useEffect(() => {
    if (variables?.productoId) {
      reexecute({ requestPolicy: 'network-only' });
      reexecuteStock({ requestPolicy: 'network-only' });
    }
  }, [variables, reexecute, reexecuteStock, refreshKey]);

  const rows = useMemo(() => {
    if (!data?.kardexPorProducto?.items?.length) {
      return [];
    }
    return data.kardexPorProducto.items.map((item) => ({
      id: item.id,
      fecha: item.fecha ? new Date(item.fecha).toLocaleString() : '—',
      tipo: item.tipoMov,
      referencia: `${item.referenciaTipo} #${item.referenciaId}`,
      cantidad: formatNumber(item.cantidad),
      costo: formatNumber(item.costoUnit),
      saldoCantidad: formatNumber(item.saldoCantidad),
      saldoValor: formatNumber(item.saldoValor),
      raw: item,
    }));
  }, [data]);

  const resumen = stockData?.stockActual;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!filters.productoId.trim()) {
      setFormError('Indicá el producto a consultar.');
      return;
    }
    setFormError('');
    setVariables({
      productoId: filters.productoId.trim(),
      sucursalId: filters.sucursalId.trim(),
      desde: filters.desde,
      hasta: filters.hasta,
      refreshKey,
    });
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Kardex"
        subtitle="Consultá el historial completo de movimientos por producto y sucursal."
        actions={
          <button
            type="button"
            onClick={() => onAdjust?.(filters.productoId.trim())}
            className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Ajustar stock
          </button>
        }
      />

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <form onSubmit={handleSubmit} className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-3">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="kardex-producto">
              Producto ID
            </label>
            <input
              id="kardex-producto"
              value={filters.productoId}
              onChange={(event) => setFilters((prev) => ({ ...prev, productoId: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="kardex-sucursal">
              Sucursal ID
            </label>
            <input
              id="kardex-sucursal"
              value={filters.sucursalId}
              onChange={(event) => setFilters((prev) => ({ ...prev, sucursalId: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="kardex-desde">
              Desde
            </label>
            <input
              type="date"
              id="kardex-desde"
              value={filters.desde}
              onChange={(event) => setFilters((prev) => ({ ...prev, desde: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="kardex-hasta">
              Hasta
            </label>
            <input
              type="date"
              id="kardex-hasta"
              value={filters.hasta}
              onChange={(event) => setFilters((prev) => ({ ...prev, hasta: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </div>
          <div className="col-span-12 flex items-end justify-end">
            <button
              type="submit"
              className="rounded-xl bg-[rgb(var(--brand))] px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Aplicar filtros
            </button>
          </div>
          {formError ? <p className="col-span-12 text-sm text-red-300">{formError}</p> : null}
        </form>
      </section>

      {variables?.productoId ? (
        <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
          {resumen ? (
            <div className="mb-4 flex flex-wrap items-center gap-6 text-sm text-[rgb(var(--fg))]">
              <span className="rounded-full bg-[rgba(var(--bg),0.65)] px-3 py-1">Stock actual: {formatNumber(resumen.cantidad)}</span>
              <span className="rounded-full bg-[rgba(var(--bg),0.65)] px-3 py-1">
                Costo promedio: ${formatNumber(resumen.valorPromedio)}
              </span>
              <span className="rounded-full bg-[rgba(var(--bg),0.65)] px-3 py-1">
                Sucursal consultada: {variables.sucursalId || 'General'}
              </span>
            </div>
          ) : null}

          <div className="overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.6)]">
            {fetching ? (
              <div className="px-4 py-6 text-sm text-muted">Cargando movimientos…</div>
            ) : error ? (
              <div className="px-4 py-6 text-sm text-red-300">{error.message}</div>
            ) : rows.length ? (
              <DataTable
                columns={columns}
                rows={rows}
                onRowClick={(row) =>
                  onViewDetalle?.({
                    productoId: variables?.productoId,
                    sucursalId: variables?.sucursalId,
                    desde: variables?.desde,
                    hasta: variables?.hasta,
                    movimiento: row.raw,
                  })
                }
                pageSize={15}
              />
            ) : (
              <div className="px-4 py-6 text-sm text-muted">No hay movimientos para los filtros seleccionados.</div>
            )}
          </div>
        </section>
      ) : null}

      <StickyActions
        left={<p className="text-sm text-muted">Cada movimiento impacta en el saldo y en la contabilidad futura.</p>}
        right={
          <button
            type="button"
            onClick={() => onAdjust?.(variables?.productoId || filters.productoId.trim())}
            className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Ajuste rápido
          </button>
        }
      />
    </div>
  );
}
